import React, { useEffect, useMemo, useRef, useState } from "react";
import { Workspace } from "polotno/canvas/workspace";
import { createStore } from "polotno/model/store";
import { rebuildPolotnoJsonSafely } from "../utils/polotnoJsonLoader";
import "./PolotnoSlideshowViewer.css";

const wait = (ms) => new Promise((resolve) => window.setTimeout(resolve, ms));

export const PolotnoSlideshowViewer = ({
  slides: providedSlides = [],
  jsonData,
  interval = 4000,
  showDots = true,
  fadeDuration = 800,
}) => {
  const [slides, setSlides] = useState([]);
  const [pageIndex, setPageIndex] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [fade, setFade] = useState(true);
  const [exportStore] = useState(() => createStore({ key: "preview-slideshow" }));
  const timerRef = useRef(null);

  useEffect(() => {
    let isMounted = true;

    const buildSlides = async () => {
      if (providedSlides.length > 0) {
        if (isMounted) {
          setSlides(
            providedSlides.map((src, index) => ({
              id: `provided-${index}`,
              src,
              name: `Page ${index + 1}`,
            }))
          );
          setPageIndex(0);
          setIsLoading(false);
        }
        return;
      }

      if (!jsonData?.pages?.length) {
        if (isMounted) {
          setSlides([]);
          setIsLoading(false);
        }
        return;
      }

      setIsLoading(true);

      try {
        try {
          await Promise.resolve(exportStore.loadJSON(jsonData));
        } catch (loadError) {
          console.error("Failed to load slideshow JSON natively, trying safe rebuild.", loadError);
          rebuildPolotnoJsonSafely(exportStore, jsonData);
        }
        await wait(360);

        const orderedPages = jsonData.pages || [];
        const renderedSlides = [];

        for (const page of orderedPages) {
          const matchingPage = exportStore.pages.find((item) => item.id === page.id);
          if (!matchingPage) continue;

          exportStore.selectPage(matchingPage.id);
          await wait(220);

          const dataUrl = await exportStore.toDataURL({
            pageId: matchingPage.id,
            pixelRatio: 2,
          });

          renderedSlides.push({
            id: matchingPage.id,
            src: dataUrl,
            name: matchingPage.name || `Page ${renderedSlides.length + 1}`,
          });
        }

        if (isMounted) {
          setSlides(renderedSlides);
          setPageIndex(0);
        }
      } catch (error) {
        console.error("Failed to build slideshow images:", error);
        if (isMounted) {
          setSlides([]);
        }
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    buildSlides();

    return () => {
      isMounted = false;
    };
  }, [exportStore, jsonData, providedSlides]);

  useEffect(() => {
    if (slides.length <= 1) return undefined;

    timerRef.current = window.setInterval(() => {
      setFade(false);
      window.setTimeout(() => {
        setPageIndex((prev) => (prev + 1) % slides.length);
        window.setTimeout(() => setFade(true), 24);
      }, Math.max(140, fadeDuration / 3));
    }, interval);

    return () => window.clearInterval(timerRef.current);
  }, [fadeDuration, interval, slides.length]);

  const goToSlide = (nextIndex) => {
    if (slides.length <= 1) return;
    window.clearInterval(timerRef.current);
    setFade(false);
    window.setTimeout(() => {
      setPageIndex(nextIndex);
      window.setTimeout(() => setFade(true), 24);
    }, Math.max(140, fadeDuration / 3));
  };

  const nextPage = () => {
    goToSlide((pageIndex + 1) % slides.length);
  };

  const prevPage = () => {
    goToSlide((pageIndex - 1 + slides.length) % slides.length);
  };

  const currentSlide = useMemo(() => slides[pageIndex], [pageIndex, slides]);

  return (
    <div className="slideshow-container">
      <div className="slideshow-export-workspace" aria-hidden="true">
        <Workspace
          store={exportStore}
          pageControlsEnabled={false}
          renderOnlyActivePage={false}
        />
      </div>

      {isLoading ? (
        <div className="slideshow-loading-state">
          <div className="slideshow-loading-frame"></div>
          <p>Preparing slideshow preview...</p>
        </div>
      ) : currentSlide ? (
        <div
          className={`slideshow-image-stage ${fade ? "fade-in" : "fade-out"}`}
          style={{ transitionDuration: `${fadeDuration}ms` }}
        >
          <img
            src={currentSlide.src}
            alt={currentSlide.name}
            className="slideshow-preview-image"
          />
        </div>
      ) : (
        <div className="slideshow-loading-state">
          <p>No slideshow pages available.</p>
        </div>
      )}

      {slides.length > 1 && (
        <>
          <button className="slideshow-nav-btn left" onClick={prevPage} title="Previous Slide">
            ‹
          </button>
          <button className="slideshow-nav-btn right" onClick={nextPage} title="Next Slide">
            ›
          </button>
        </>
      )}

      {showDots && slides.length > 1 && (
        <div className="dot-container">
          {slides.map((slide, index) => (
            <button
              key={slide.id}
              type="button"
              className={`dot ${index === pageIndex ? "active" : ""}`}
              onClick={() => goToSlide(index)}
              aria-label={`Go to slide ${index + 1}`}
            />
          ))}
        </div>
      )}
    </div>
  );
};
